import { MODAL_AUTH } from '@components/App/Modal/constants'
import { PasswordInput } from '@components/App/PasswordInput'
import { Input } from '@lib/components'
import { Button } from '@octopus-do/octopus-storybook'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { replace } from 'redux-first-history'

import { resetPassword } from '@/services/password'
import useValidate from './hooks/useValidate'
import css from './PasswordReset.module.scss'
import type { Errors } from './types'

type Props = {
  resetId: string
  closeButton: React.ReactNode
}

const PasswordReset: React.FC<Props> = ({ resetId, closeButton }) => {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)

  const [newPassword, setNewPassword] = useState('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('')

  const [errors, setErrors] = useState<Errors | null>(null)
  const [loading, setLoading] = useState(false)

  const validate = useValidate()

  const onChangeNewPassword = (value: string) => {
    setErrors({
      ...errors,
      newPassword: null,
    })

    setNewPassword(value)
  }

  const onChangeNewPasswordRepeat = (value: string) => {
    setErrors({
      ...errors,
      newPasswordRepeat: null,
    })

    setNewPasswordRepeat(value)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const { isValid, validationErrors } = validate({
      newPassword,
      newPasswordRepeat,
    })

    if (!isValid) {
      setErrors(validationErrors)

      return
    }

    setLoading(true)

    try {
      await resetPassword({
        hash: resetId,
        password: newPassword,
        repeat_password: newPasswordRepeat,
      })

      dispatch(replace('/'))

      dispatch(openModal(MODAL_AUTH))

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.RESET_PASSWORD_SUCCESS,
        })
      )
    } catch (error: any) {
      setErrors({
        ...errors,
        newPassword: error.response.data.message,
      })

      dispatch(replace('/'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    inputRef?.current?.focus()
  }, [])

  return (
    <div className={css.container}>
      <div className={css.heading}>
        <div className={css.title}>Set new password</div>
        <p className={css.description}>
          Enter new password and then repeat it.
        </p>

        <div className={css.close}>{closeButton}</div>
      </div>

      <form className={css.form} onSubmit={onSubmit}>
        <div className={css.input}>
          <PasswordInput
            name="newPassword"
            id="newPassword"
            label="New password"
            onChange={(value) => onChangeNewPassword(value)}
            error={errors?.newPassword as string}
          />
        </div>

        <div className={css.input}>
          <Input
            label="New password repeat"
            id="newPasswordRepeat"
            name="newPasswordRepeat"
            htmlType="password"
            type="normal"
            value={newPasswordRepeat}
            onChange={(value) => onChangeNewPasswordRepeat(value)}
          />

          {errors?.newPasswordRepeat && (
            <small>{errors.newPasswordRepeat}</small>
          )}
        </div>

        <Button type="submit" label="Save" loading={loading} />
      </form>
    </div>
  )
}

export default PasswordReset
